<template>
    <section class="bill-summary-section">
        <TotalPaidLabel v-if="displayTotalPaidLabel" :totalPaid="bill.totalPaid" />

        <ScrollableView class="bill-summary-content">
            <section class="no-order-yet-msg" v-if="displayEverythingIsPaidMsg">
                {{ allocation.allocation?.tableName }}: {{ trans_vue.bill.noOrderYet }}
            </section>
            <section class="unpaid-line-items-list" v-if="displayUnpaidItems" ref="unpaidItemsSection">
                <UnpaidLineItem
                    v-for="item in bill.unpaidItems"
                    :key="item.id"
                    :item="item"
                    :isSelectionMode="bill.isAnyItemSelected"
                    :isSelectionEnabled="!isCartSelectionActive"
                    :quantityDigits="unpaidItemsQuantityDigits"
                    :disabled="!allocation.hasPaymentServices"
                    :showInternalNames="true"
                    :isUserWaiterOrAdmin="true"
                    @selectionChange="onItemSelectionChange"
                    @onClickOpenOrderDetails="onClickOpenOrderDetails"
                />
            </section>

            <section v-if="displayCartItems" ref="cartSection">
                <h3>{{ trans_vue.cartItemsList.cart }}</h3>
                <CartItemsList :showInternalNames="true" />
            </section>

            <template v-if="displayPaidItems">
                <h3>{{ trans_vue.bill.fullyPaidItems }}</h3>
                <section class="paid-line-items-list">
                    <PaidLineItem
                        v-for="item in bill.paidItems"
                        :key="item.id"
                        :item="item"
                        :showInternalNames="true"
                        :isUserWaiterOrAdmin="true"
                        :quantityDigits="paidItemsQuantityDigits"
                        :onClickOpenOrderDetails="
                            (orderId: string) => {
                                onClickOpenOrderDetails(orderId);
                            }
                        "
                    />
                </section>
            </template>

            <div v-if="displayNoPaymentServiceMsg" class="no-payment-service-msg">
                {{ trans_vue.billSection.noPaymentServiceConfigured }}
            </div>
        </ScrollableView>

        <footer>
            <SelectAllButton :isShown="displaySelectAllButton" @click="selectAll" />
            <ClearSelectionButton :isShown="displayClearSelectionButton" @click="clearSelection" />

            <TipSummaryTable
                v-if="displayCartItems"
                :tip="cartPayment.tip"
                :hasDiscounts="cart.hasDiscounts"
                :fees="cart.feesAmount"
                :discountAmount="cart.totalDiscountsAndVouchersAmount"
                :totalConsumption="cart.totalAmount"
                :showTip="true"
                :isDiscountClickable="true"
                @discountClick="onDiscountClick"
            />
            <CartDiscountListModal ref="discountListModalComponent" @discountRemoved="onDiscountRemoved" />
        </footer>
    </section>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Bill } from "@/data/model/Bill";
    import { UnpaidLineItem as UnpaidLineItemEntity } from "@/data/model/Bill";
    import { useBillStore } from "@/data/stores/bill";
    import { useCartStore } from "@/data/stores/cart";
    import { useCartPaymentStore } from "@/data/stores/cartPayment";
    import { useOfferStore } from "@/data/stores/offer";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { trans_vue } from "@/operator/localization/TransVue";
    import { OrderDetailsModal } from "@/operator/modal/OrderDetailsModal";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, ref, watch } from "vue";

    import PaidLineItem from "@/components/bill/PaidLineItem.vue";
    import TotalPaidLabel from "@/components/bill/TotalPaidLabel.vue";
    import UnpaidLineItem from "@/components/bill/UnpaidLineItem.vue";
    import CartItemsList from "@/components/cart/CartItemsList.vue";
    import ClearSelectionButton from "@/components/elements/ClearSelectionButton.vue";
    import SelectAllButton from "@/components/elements/SelectAllButton.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import TipSummaryTable from "@/components/pay/TipSummaryTable.vue";
    import CartDiscountListModal from "@/operator/components/cart/CartDiscountListModal.vue";

    // define stores
    const cart = useCartStore();
    const cartPayment = useCartPaymentStore();
    const bill = useBillStore();
    const allocation = useTableAllocationStore();
    const offer = useOfferStore();

    // define computed
    const unpaidItemsQuantityDigits = computed(() => Bill.getUnpaidMaxDisplayedDigits(bill.unpaidItems));
    const paidItemsQuantityDigits = computed(() => Bill.getPaidMaxDisplayedDigits(bill.paidItems));
    const isEverythingPaid = computed(() => !bill.hasUnpaidItems && !bill.hasPaidItems && !cart.hasItems);

    const displayTotalPaidLabel = computed(() => allocation.hasPaymentServices && bill.hasPaidItems);
    const displayUnpaidItems = computed(() => allocation.hasPaymentServices && bill.hasUnpaidItems);
    const displayPaidItems = computed(() => allocation.hasPaymentServices && bill.hasPaidItems);
    // cart items are only selectable to feed the discount button, so without discounts the cart
    // never takes selection over and everything stays on the bill
    const isCartSelectionActive = computed(() => cart.hasItems && offer.areDiscountsEnabled);
    const displaySelectAllButton = computed(() =>
        isCartSelectionActive.value
            ? !cart.isAnyCartItemSelected
            : displayUnpaidItems.value && !bill.isAnyItemSelected
    );
    const displayClearSelectionButton = computed(() =>
        isCartSelectionActive.value
            ? cart.isAnyCartItemSelected
            : displayUnpaidItems.value && bill.isAnyItemSelected
    );
    const displayEverythingIsPaidMsg = computed(() => allocation.hasPaymentServices && isEverythingPaid.value);

    const displayCartItems = computed(() => cart.hasItems);
    const displayNoPaymentServiceMsg = computed(() => !allocation.hasPaymentServices);

    // define watch
    // as soon as the cart takes selection over, the bill items are no longer selectable, so any
    // selection made before that has to be dropped (on the server too) to stay in sync
    const hasBillSelectionWhileCartSelectionActive = computed(
        () => isCartSelectionActive.value && bill.isAnyItemSelected
    );

    watch(
        hasBillSelectionWhileCartSelectionActive,
        async (hasBillSelection) => {
            if (!hasBillSelection) return;

            try {
                await bill.deselectAllItems();
            } catch (ex) {
                handleInvalidSelection(ex);
            }
        },
        { immediate: true }
    );

    // define methods
    async function selectAll() {
        if (isCartSelectionActive.value) {
            cart.selectAllCartItems();
            focusOnCartSection();
            return;
        }

        try {
            await bill.selectAllItems();
            focusOnUnpaidItemsSection();
        } catch (ex) {
            handleInvalidSelection(ex);
        }
    }

    async function clearSelection() {
        if (isCartSelectionActive.value) {
            cart.deselectAllCartItems();
            focusOnCartSection();
            return;
        }

        try {
            await bill.deselectAllItems();
            focusOnUnpaidItemsSection();
        } catch (ex) {
            handleInvalidSelection(ex);
        }
    }

    async function onClickOpenOrderDetails(orderId: string) {
        new OrderDetailsModal(orderId, () => {}).show();
    }

    async function onItemSelectionChange(data: { item: UnpaidLineItemEntity; selectedQuantity: number }) {
        try {
            await bill.selectItem(data.item, data.selectedQuantity);
        } catch (ex) {
            handleInvalidSelection(ex);
        }
    }

    function focusOnCartSection() {
        if (cartSection.value) {
            const lastAddedCartItem = cartSection.value.querySelector(".cart-item:last-child");
            if (lastAddedCartItem) {
                lastAddedCartItem.scrollIntoView({ behavior: "smooth" });
            } else {
                cartSection.value.scrollIntoView({ behavior: "smooth" });
            }
        }
    }

    function focusOnUnpaidItemsSection() {
        if (unpaidItemsSection.value) unpaidItemsSection.value.scrollIntoView({ behavior: "smooth" });
    }

    function handleInvalidSelection(e: unknown) {
        logError(e);

        Popup.show({
            uniqueId: "pmtconfirm",
            message: translations.pay.failedBillInvalidSelection,
            confirmButton: translations.general.ok,
            confirmHandler: async () => {
                await bill.loadCurrentBill();
            },
        });
    }

    // define html refs
    const discountListModalComponent = ref<InstanceType<typeof CartDiscountListModal> | null>(null);

    function onDiscountClick() {
        discountListModalComponent.value?.show();
    }

    function onDiscountRemoved() {}

    // define template refs
    const cartSection = ref<HTMLElement | null>(null);
    const unpaidItemsSection = ref<HTMLElement | null>(null);

    // define exposed
    defineExpose({
        focusOnCartSection,
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";

    .bill-summary-section {
        position: relative;
        display: flex;
        flex-direction: column;

        > .bill-summary-content {
            flex: 1 1 auto;

            > .scrollable-content > section {
                padding-bottom: 1em;
            }
        }

        .unpaid-line-items-list {
            > :first-child {
                padding-top: 1.25em;
            }
        }

        h3 {
            margin-top: 1em;
            margin-bottom: 0.5em;
            padding: 0 1.666em;
            padding-top: 0.5em;
            padding-bottom: 0.5em;
            border-bottom: 1px solid colors.$lightGreyBorder;
            font-size: 14px;
            font-weight: 700;
        }

        .paid-line-items-list {
            padding-bottom: 4em !important;
        }

        .no-order-yet-msg {
            padding: 2em;
            text-align: center;
        }

        section + .no-payment-service-msg {
            padding-top: 0.75em;
        }

        .no-payment-service-msg {
            padding-top: 3.5em;
            width: 100%;
            text-align: center;
        }

        > footer {
            position: relative;

            .tip-summary-table {
                padding-left: 1.25em;
                padding-right: 1.25em;
            }
        }
    }
</style>
